/**
 * CollaborativeDocumentEditor - Tiptap v3 + Yjs CRDT collaborative editor.
 *
 * Features:
 *   - Real-time collaboration via YjsWebPubSubProvider over Azure Web PubSub
 *   - Deterministic clientID for initial HTML to prevent content duplication
 *   - contentReady gate: editor is non-editable until initial content loads
 *   - Loading overlay with spinner while syncing
 *   - Auto-save with configurable interval
 *   - Rich text toolbar (headings, formatting, lists, tables, alignment, etc.)
 *   - Word count in status bar
 *   - Read-only mode with configurable reason message
 *   - Presence tracking with collaborator avatars
 *   - Expanded (fullscreen-like) mode support
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import Typography from '@tiptap/extension-typography';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import FontFamily from '@tiptap/extension-font-family';
import { Extension } from '@tiptap/core';
import Collaboration from '@tiptap/extension-collaboration';
import { yCursorPlugin } from '@tiptap/y-tiptap';
import * as Y from 'yjs';
import { Awareness } from 'y-protocols/awareness';

import { YjsWebPubSubProvider, getInitialContentClientID } from '../../services/YjsWebPubSubProvider';
import ProfileImage from '../Header/ProfileImage';
// @ts-ignore css import
import './CollaborativeDocumentEditor.css';

import {
  PiTextBLight as PiBoldIcon,
  PiTextItalicLight as PiItalicIcon,
  PiTextUnderlineLight as PiUnderlineIcon,
  PiTextStrikethroughLight as PiStrikethroughIcon,
  PiTextAlignLeftLight,
  PiTextAlignCenterLight,
  PiTextAlignRightLight,
  PiTextAlignJustifyLight,
  PiListBulletsLight,
  PiListNumbersLight,
  PiQuotesLight,
  PiCodeLight,
  PiMinusLight,
  PiTableLight,
  PiTextSuperscriptLight as PiSuperscriptIcon,
  PiTextSubscriptLight as PiSubscriptIcon,
  PiAtLight,
  PiArrowUUpLeftLight,
  PiArrowUUpRightLight,
  PiSpinnerGapLight,
  PiLock,
  PiLockLight,
} from 'react-icons/pi';
import Spinner from './TailwindComponents/Spinner';

// ─── Collaborator colors ─────────────────────────────────────────────

const COLLABORATOR_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
];

function getCollaboratorColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLLABORATOR_COLORS[Math.abs(hash) % COLLABORATOR_COLORS.length];
}

// ─── Props ───────────────────────────────────────────────────────────

interface CollaborativeDocumentEditorProps {
  /** Matter UUID - used for collaboration room */
  matterUuid: string;
  /** Document type ID (e.g. "affidavits_affidavit_of_service") */
  documentId: string;
  /** Initial HTML content from the backend */
  initialHtml: string;
  /** Lossless Tiptap JSON to seed the editor with. When present (and fresh, as
   *  validated server-side) this is preferred over initialHtml, because the
   *  markdown→HTML round-trip loses structure (tables, marks, nesting). */
  initialJson?: unknown;
  /** Current user info */
  user: {
    id: string;
    name: string;
    email?: string;
    profilePicture?: string;
  };
  /** Whether real-time collaborative editing is enabled. When false, the editor
   *  never opens the Yjs/Web PubSub socket and runs as a single-user editor
   *  (local load + REST save). Defaults to true. */
  collabEnabled?: boolean;
  /** Whether editor is read-only */
  readOnly?: boolean;
  /** Reason for read-only (shown in toolbar) */
  readOnlyReason?: string;
  /** Called to persist an edit with (html, json). May return a promise; the
   *  editor awaits it to show real Saving/Saved/Failed status. */
  onSave?: (html: string, json: any, saveKind?: 'auto' | 'manual') => void | Promise<unknown>;
  /** Auto-save debounce interval in ms */
  autoSaveInterval?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the editor is in expanded/fullscreen mode */
  isExpanded?: boolean;
  /** Callback for active users list changes */
  onActiveUsersChange?: (users: Array<{ id: string; name: string; color: string }>) => void;
}

// ─── Component ───────────────────────────────────────────────────────

const CollaborativeDocumentEditor: React.FC<CollaborativeDocumentEditorProps> = ({
  matterUuid,
  documentId,
  initialHtml,
  initialJson,
  user,
  collabEnabled = true,
  readOnly = false,
  readOnlyReason,
  onSave,
  autoSaveInterval = 3000,
  placeholder,
  isExpanded = false,
  onActiveUsersChange,
}) => {
  const { t } = useTranslation();
  const [connected, setConnected] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [contentReady, setContentReady] = useState(false);
  // Honest save state - replaces the old always-on "✓ Saved" label so the user
  // can actually trust what the status bar says.
  const [saveStatus, setSaveStatus] = useState<'idle' | 'unsaved' | 'saving' | 'saved' | 'error'>('idle');
  // Other people currently in the document (from Yjs awareness), for the live
  // "who's here" avatar stack.
  const [activeUsers, setActiveUsers] = useState<Array<{ id: string; name: string; color: string; profilePicture?: string }>>([]);

  // Refs for cleanup and debounce
  const providerRef = useRef<YjsWebPubSubProvider | null>(null);
  const ydocRef = useRef<Y.Doc | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialHtmlRef = useRef(initialHtml);
  const initialJsonRef = useRef(initialJson);
  const mountedRef = useRef(true);
  // Edit-persistence bookkeeping (kept in refs so closures + unmount see live values).
  const editorRef = useRef<any>(null);
  const onSaveRef = useRef(onSave);
  const readOnlyRef = useRef(readOnly);
  const autoSaveIntervalRef = useRef(autoSaveInterval);
  const dirtyRef = useRef(false);          // unsaved changes since the last successful save
  const savingRef = useRef(false);         // a save is currently in flight
  const editSeqRef = useRef(0);            // bumped on every edit; guards against clearing dirty when edits arrived mid-save
  const pendingSaveRef = useRef(false);    // an edit came in while a save was in flight → save again after
  const connectedRef = useRef(false);      // live collab socket state (for the offline fallback)
  const contentLoadedRef = useRef(false);  // initial content has been placed exactly once
  const performSaveRef = useRef<(kind?: 'auto' | 'manual') => void>(() => {});

  // Keep refs current for closures / cleanup.
  initialHtmlRef.current = initialHtml;
  initialJsonRef.current = initialJson;
  onSaveRef.current = onSave;
  readOnlyRef.current = readOnly;
  autoSaveIntervalRef.current = autoSaveInterval;

  // ── Create Y.Doc and provider (once per mount, in useEffect for StrictMode safety) ──

  const ydoc = useMemo(() => new Y.Doc(), []);
  ydocRef.current = ydoc;

  // Create awareness synchronously so CollaborationCursor has it at plugin init time
  const awareness = useMemo(() => {
    const a = new Awareness(ydoc);
    return a;
  }, [ydoc]);

  // Set local awareness state with user info - must be set before yCursorPlugin reads it.
  // IMPORTANT: Use setLocalState (not setLocalStateField) because after React
  // StrictMode double-mount cleanup, the awareness state may be null (deleted by
  // removeAwarenessStates in the previous provider's destroy). setLocalStateField
  // silently no-ops when getLocalState() is null.
  useEffect(() => {
    const color = getCollaboratorColor(user.id);
    const currentState = awareness.getLocalState() || {};
    awareness.setLocalState({
      ...currentState,
      user: {
        id: user.id,
        name: user.name,
        color,
        email: user.email || '',
        profilePicture: user.profilePicture || '',
      },
    });
  }, [awareness, user.id, user.name, user.email, user.profilePicture]);

  // ── Track awareness state → propagate active users to parent ──────

  useEffect(() => {
    const updateUsers = () => {
      const users: Array<{ id: string; name: string; color: string; email?: string; profilePicture?: string }> = [];
      awareness.getStates().forEach((state, clientId) => {
        // Skip self
        if (clientId === ydoc.clientID) return;
        const u = state.user;
        if (u && u.name) {
          users.push({
            id: u.id || String(clientId),
            name: u.name,
            color: u.color || '#6b7280',
            email: u.email,
            profilePicture: u.profilePicture,
          });
        }
      });
      // De-dupe by user id (one person can hold several awareness client ids
      // across tabs/reconnects - the avatar stack should show them once).
      const byId = new Map<string, { id: string; name: string; color: string; profilePicture?: string }>();
      users.forEach((u) => { if (!byId.has(u.id)) byId.set(u.id, u); });
      const deduped = Array.from(byId.values());
      setActiveUsers(deduped);
      if (onActiveUsersChange) onActiveUsersChange(deduped);
    };

    awareness.on('change', updateUsers);
    // Fire once immediately
    updateUsers();

    return () => {
      awareness.off('change', updateUsers);
    };
  }, [awareness, ydoc.clientID, onActiveUsersChange]);

  // ── Tiptap Editor ─────────────────────────────────────────────────

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // @ts-ignore - Yjs handles undo/redo; disable built-in history
        history: false,
      }),
      Placeholder.configure({ placeholder: placeholder ?? t('Start typing…') }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Superscript,
      Subscript,
      Typography,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList,
      TaskItem.configure({ nested: true }),
      FontFamily,
      Collaboration.configure({
        document: ydoc,
        field: 'default',
      }),
      // Custom extension: use yCursorPlugin from @tiptap/y-tiptap (same package
      // as ySyncPlugin) so both share the same ySyncPluginKey instance.
      // Using @tiptap/extension-collaboration-cursor would import yCursorPlugin
      // from y-prosemirror, which creates a SEPARATE ySyncPluginKey - causing
      // "Cannot read properties of undefined (reading 'doc')" at createDecorations.
      Extension.create({
        name: 'collaborationCursor',
        addProseMirrorPlugins() {
          return [yCursorPlugin(awareness)];
        },
      }),
    ],
    editable: !readOnly && contentReady,
    onUpdate: ({ editor: ed }) => {
      // Compute word count
      const text = ed.state.doc.textContent;
      setWordCount(text.split(/\s+/).filter(Boolean).length);

      // Mark dirty + schedule a debounced save. The actual save goes through
      // performSaveRef so the same code path runs for autosave, blur and unmount.
      if (!readOnlyRef.current && onSaveRef.current) {
        dirtyRef.current = true;
        editSeqRef.current += 1;
        if (mountedRef.current) setSaveStatus('unsaved');
        // Debounced idle-save: the timer is reset on every keystroke, so the
        // save only fires once the user has paused for `autoSaveInterval` ms.
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => performSaveRef.current(), autoSaveIntervalRef.current);
      }
    },
    onBlur: () => {
      // Clicking away is a strong "I'm done for now" signal - flush immediately
      // rather than waiting out the debounce, so a toggle-to-View can't race it.
      if (readOnlyRef.current || !onSaveRef.current || !dirtyRef.current) return;
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
      performSaveRef.current();
    },
  });

  editorRef.current = editor;

  // ── The single save path (autosave / blur / unmount all call this) ─────
  const performSave = useCallback(async (kind: 'auto' | 'manual' = 'auto') => {
    const ed = editorRef.current;
    const save = onSaveRef.current;
    if (!ed || !save || readOnlyRef.current) return;
    if (!dirtyRef.current) return;
    // A save is already in flight - don't drop this request; mark it so the
    // in-flight save runs one more pass when it finishes (captures edits made
    // during the save). This is what stops "typed while saving" edits vanishing.
    if (savingRef.current) { pendingSaveRef.current = true; return; }

    // ── SAFETY: never overwrite a document with empty content ──────────────
    // If the editor is blank - most importantly when the initial content failed
    // to load - refusing to save is what stops an empty editor from wiping an
    // existing draft. Only content the user actively cleared to nothing is
    // affected, and that is not something we ever want autosave to persist.
    let html: string;
    let json: unknown;
    try {
      html = ed.getHTML();
      json = ed.getJSON();
    } catch {
      return;
    }
    if (ed.state.doc.textContent.trim().length === 0) {
      // Leave it flagged unsaved so the status bar is honest, but do NOT persist.
      if (mountedRef.current) setSaveStatus('unsaved');
      return;
    }

    savingRef.current = true;
    pendingSaveRef.current = false;
    const seqAtStart = editSeqRef.current;  // snapshot: what this save is persisting
    if (mountedRef.current) setSaveStatus('saving');
    try {
      await Promise.resolve(save(html, json, kind));
      // Only clear dirty if NO new edit landed while we were saving. If the user
      // kept typing during the request, keep dirty=true so the follow-up below
      // persists the newer content - never mark "saved" over unsaved edits.
      if (editSeqRef.current === seqAtStart) {
        dirtyRef.current = false;
        if (mountedRef.current) setSaveStatus('saved');
      } else {
        pendingSaveRef.current = true;
      }
    } catch {
      // Keep dirty=true so the next autosave/blur/unmount retries - the edit is
      // never silently dropped.
      if (mountedRef.current) setSaveStatus('error');
    } finally {
      savingRef.current = false;
      // A save was requested (or edits arrived) during this one - run once more.
      if (pendingSaveRef.current && dirtyRef.current) {
        pendingSaveRef.current = false;
        setTimeout(() => performSaveRef.current(kind), 0);
      }
    }
  }, []);
  performSaveRef.current = performSave;

  // Flush any pending edit when the tab/window is closing (best-effort).
  useEffect(() => {
    const handler = () => {
      if (dirtyRef.current) {
        if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
        performSaveRef.current();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Collaboration is best-effort: we try to connect, but the editor NEVER
  // depends on the socket to function. If we haven't connected shortly after
  // mount, load the content locally and edit OFFLINE - REST save is the source
  // of truth either way. A longer backstop covers the "connected but sync stuck"
  // case so editing can never hang.
  useEffect(() => {
    // Only relevant when collaboration is on - this is the fallback for a slow/
    // failed socket connect. Single-user mode loads content in its own effect.
    if (readOnly || !collabEnabled) return;
    const loadOffline = () => {
      if (!mountedRef.current || contentLoadedRef.current) return;
      try {
        const seed = initialJsonRef.current;
        if (seed && typeof seed === 'object') {
          // Lossless Tiptap JSON - round-trips tables/marks/nesting exactly.
          editorRef.current?.commands.setContent(seed as any, { emitUpdate: false });
        } else {
          editorRef.current?.commands.setContent(initialHtmlRef.current, { emitUpdate: false });
        }
      } catch { /* editor not ready - the sync path will still load it */ }
      contentLoadedRef.current = true;
      if (mountedRef.current) setContentReady(true);
    };
    // Not connected quickly → go offline and load now (fast path for the common
    // single-user / socket-down case).
    const fast = setTimeout(() => { if (!connectedRef.current) loadOffline(); }, 2000);
    // Connected but sync never settled (e.g. the collab UPSTREAM server is down
    // even though the WebSocket transport connected) → load anyway rather than
    // sit on "Connecting…". Short, so it never feels stuck.
    const backstop = setTimeout(loadOffline, 3000);
    return () => { clearTimeout(fast); clearTimeout(backstop); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, collabEnabled]);

  // Single-user mode (collaboration disabled): never open the socket. Load the
  // document locally as soon as the editor exists and mark it ready; edits are
  // persisted over REST via `onSave`, exactly like the offline fallback but with
  // no connection attempt at all.
  useEffect(() => {
    if (collabEnabled || readOnly || !editor) return;
    if (!contentLoadedRef.current) {
      try {
        const seed = initialJsonRef.current;
        if (seed && typeof seed === 'object') {
          editor.commands.setContent(seed as any, { emitUpdate: false });
        } else {
          editor.commands.setContent(initialHtmlRef.current, { emitUpdate: false });
        }
      } catch { /* ignore - nothing else will overwrite it in single-user mode */ }
      contentLoadedRef.current = true;
    }
    connectedRef.current = false;
    setConnected(false);
    setContentReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collabEnabled, readOnly, editor]);

  // ── Sync editable state ───────────────────────────────────────────

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly && contentReady);
    }
  }, [editor, readOnly, contentReady]);

  // ── Load initial content into Y.Doc and connect provider ──────────

  useEffect(() => {
    // Collaboration off → never create the provider or open the socket. The
    // single-user effect above has already loaded the document.
    if (!editor || !ydoc || !collabEnabled) return;
    mountedRef.current = true;

    const color = getCollaboratorColor(user.id);
    const provider = new YjsWebPubSubProvider({
      doc: ydoc,
      matterId: matterUuid,
      documentId,
      awareness, // Share the same awareness instance with CollaborationCursor
      user: {
        id: user.id,
        name: user.name,
        color,
        email: user.email || '',
        profilePicture: user.profilePicture || '',
      },
    });
    providerRef.current = provider;

    // Connection status callback
    provider.onConnectionChange((isConnected) => {
      connectedRef.current = isConnected;
      if (mountedRef.current) setConnected(isConnected);
    });

    // Synced callback - when sync step 2 arrives (or timeout)
    provider.onSyncedChange((isSynced) => {
      if (!mountedRef.current) return;

      // If the safety-net timeout already placed content, don't load it twice.
      if (contentLoadedRef.current) {
        setContentReady(true);
        return;
      }

      // Check if existing Yjs doc has content from peers
      const fragment = ydoc.getXmlFragment('default');
      const existingContent = fragment.toJSON();
      const hasRemoteContent = existingContent && existingContent.length > 0 &&
        !(existingContent.length === 1 && existingContent[0] === '<paragraph></paragraph>');

      if (hasRemoteContent) {
        // Peers sent us content - use it (don't load initialHtml)
        console.log('[CollaborativeDocumentEditor] Using synced content from peers');
        // Compute word count from synced content
        const text = editor.state.doc.textContent;
        setWordCount(text.split(/\s+/).filter(Boolean).length);
      } else {
        // No remote content - load initialHtml with deterministic clientID
        console.log('[CollaborativeDocumentEditor] No remote content, loading initialHtml');
        const savedClientID = ydoc.clientID;
        ydoc.clientID = getInitialContentClientID();
        try {
          const seed = initialJsonRef.current;
          if (seed && typeof seed === 'object') {
            editor.commands.setContent(seed as any, { emitUpdate: false });
          } else {
            editor.commands.setContent(initialHtmlRef.current, { emitUpdate: false });
          }
          const text = editor.state.doc.textContent;
          setWordCount(text.split(/\s+/).filter(Boolean).length);
        } finally {
          ydoc.clientID = savedClientID;
        }
      }

      contentLoadedRef.current = true;
      if (mountedRef.current) setContentReady(true);
    });

    // Connect the provider
    provider.connect();

    return () => {
      // Provider teardown only. The pending-edit FLUSH is handled by the
      // always-run cleanup effect below, so it also fires in single-user mode
      // (where this collab effect early-returns and never registered a cleanup).
      setContentReady(false);
      setConnected(false);
      provider.destroy();
      providerRef.current = null;
    };
  }, [editor, ydoc, matterUuid, documentId, user.id, user.name, user.email, user.profilePicture, collabEnabled]);

  // ── Flush-on-unmount, ALWAYS (collab or single-user) ───────────────
  // Defined after the provider effect so, on unmount, this cleanup runs FIRST —
  // it fires the pending save synchronously (the request outlives the unmount)
  // before the provider is destroyed. This is the fix for edits being dropped on
  // a toggle-to-View / navigate-away within the debounce window: the flush used
  // to live only in the collab effect, which is skipped when collaboration is off.
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
      // Force the final save even if one is mid-flight - last-write-wins on the
      // same blob, and losing the newest edit is far worse than a redundant POST.
      if (dirtyRef.current) {
        savingRef.current = false;
        performSaveRef.current();
      }
    };
  }, []);

  // ── Toolbar helpers ───────────────────────────────────────────────

  const ToolbarButton: React.FC<{
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
  }> = useCallback(({ onClick, active, disabled, title, children }) => (
    <button
      onClick={onClick}
      className={active ? 'is-active' : ''}
      disabled={disabled || readOnly || !contentReady}
      title={title}
      type="button"
    >
      {children}
    </button>
  ), [readOnly, contentReady]);

  // ── Render ────────────────────────────────────────────────────────

  if (!editor) return null;

  return (
    <div className={`document-editor-page ${isExpanded ? 'expanded' : ''}`}>
      {/* Loading overlay - shown while connecting/syncing */}
      {!contentReady && !readOnly && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-bg/80 backdrop-blur-[2px]">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="glyph" className="!h-6 !w-6" />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {!collabEnabled ? t('Loading document...') : connected ? t('Syncing document...') : t('Connecting...')}
            </span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="document-editor-toolbar">
        {readOnly ? (
          <div className="flex items-center gap-2 px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
            <PiLockLight className="w-4 h-4" />
            <span>{readOnlyReason ? `Read-only - ${readOnlyReason}` : 'Read-only mode'}</span>
          </div>
        ) : (
          <>
            {/* Heading select */}
            <select
              className="toolbar-select"
              value={
                editor.isActive('heading', { level: 1 }) ? 'h1' :
                editor.isActive('heading', { level: 2 }) ? 'h2' :
                editor.isActive('heading', { level: 3 }) ? 'h3' :
                editor.isActive('heading', { level: 4 }) ? 'h4' :
                'paragraph'
              }
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'paragraph') {
                  editor.chain().focus().setParagraph().run();
                } else {
                  const level = parseInt(val.replace('h', '')) as 1 | 2 | 3 | 4;
                  editor.chain().focus().toggleHeading({ level }).run();
                }
              }}
              disabled={!contentReady}
            >
              <option value="paragraph">{t('Normal text')}</option>
              <option value="h1">{t('Heading 1')}</option>
              <option value="h2">{t('Heading 2')}</option>
              <option value="h3">{t('Heading 3')}</option>
              <option value="h4">{t('Heading 4')}</option>
            </select>

            <div className="toolbar-divider" />

            {/* Formatting */}
            <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title={t('Bold')}>
              <PiBoldIcon className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title={t('Italic')}>
              <PiItalicIcon className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title={t('Underline')}>
              <PiUnderlineIcon className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title={t('Strikethrough')}>
              <PiStrikethroughIcon className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarButton onClick={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive('superscript')} title={t('Superscript')}>
              <PiSuperscriptIcon className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive('subscript')} title={t('Subscript')}>
              <PiSubscriptIcon className="w-4 h-4" />
            </ToolbarButton>

            <div className="toolbar-divider" />

            {/* Alignment */}
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title={t('Align left')}>
              <PiTextAlignLeftLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title={t('Align center')}>
              <PiTextAlignCenterLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title={t('Align right')}>
              <PiTextAlignRightLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title={t('Justify')}>
              <PiTextAlignJustifyLight className="w-4 h-4" />
            </ToolbarButton>

            <div className="toolbar-divider" />

            {/* Lists */}
            <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title={t('Bullet list')}>
              <PiListBulletsLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title={t('Numbered list')}>
              <PiListNumbersLight className="w-4 h-4" />
            </ToolbarButton>

            <div className="toolbar-divider" />

            {/* Block elements */}
            <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title={t('Blockquote')}>
              <PiQuotesLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title={t('Code block')}>
              <PiCodeLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title={t('Horizontal rule')}>
              <PiMinusLight className="w-4 h-4" />
            </ToolbarButton>

            {/* Table */}
            <ToolbarButton onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title={t('Insert table')}>
              <PiTableLight className="w-4 h-4" />
            </ToolbarButton>

            <div className="toolbar-divider" />

            {/* Undo / Redo */}
            <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title={t('Undo')}>
              <PiArrowUUpLeftLight className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title={t('Redo')}>
              <PiArrowUUpRightLight className="w-4 h-4" />
            </ToolbarButton>

            {/* Live "who's here" - one avatar per active collaborator, coloured
                to match their cursor so it's clear who's who in the document. */}
            {activeUsers.length > 0 && (
              <div
                className="ms-auto flex items-center -space-x-2 pe-1"
                title={t('{{count}} collaborating now', { count: activeUsers.length })}
              >
                {activeUsers.slice(0, 5).map((u) => (
                  <span
                    key={u.id}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[10px] font-semibold text-white dark:border-gray-900"
                    style={{ backgroundColor: u.color }}
                    title={u.name}
                  >
                    {(u.name || '?').trim().charAt(0).toUpperCase()}
                  </span>
                ))}
                {activeUsers.length > 5 && (
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-gray-400 text-[10px] font-semibold text-white dark:border-gray-900">
                    +{activeUsers.length - 5}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Editor content */}
      <div className="document-editor-content">
        <EditorContent editor={editor} />
      </div>

      {/* Status bar */}
      <div className="document-editor-statusbar">
        {/* The Connected/Offline indicator only makes sense when collaboration
            is on. In single-user mode there is no socket, so showing "Offline"
            would be misleading - omit it entirely. */}
        {collabEnabled && (
          <span className={`flex items-center gap-1.5 ${connected ? 'text-success-600 dark:text-success-400' : 'text-gray-400'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-success-500' : 'bg-gray-400'}`} />
            {connected ? t('Connected') : t('Offline')}
          </span>
        )}

        <span>{t('{{count}} words', { count: wordCount })}</span>

        {onSave && !readOnly && (
          <span
            className={
              saveStatus === 'error'
                ? 'text-danger-600 dark:text-danger-400'
                : saveStatus === 'saved'
                  ? 'text-success-600 dark:text-success-400'
                  : 'text-gray-400'
            }
          >
            {saveStatus === 'saving'
              ? t('Saving…')
              : saveStatus === 'saved'
                ? t('✓ Saved')
                : saveStatus === 'error'
                  ? t('Save failed')
                  : saveStatus === 'unsaved'
                    ? t('Unsaved changes…')
                    : t('Saved')}
          </span>
        )}

        {/* Explicit Save - always available in edit mode. Autosaves are frequent
            but kept OUT of the Versions panel; clicking Save writes a milestone
            version the user can restore to. */}
        {onSave && !readOnly && (
          <button
            type="button"
            disabled={saveStatus === 'saving'}
            onClick={() => { if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; } dirtyRef.current = true; performSaveRef.current('manual'); }}
            className="ms-2 rounded px-2 py-0.5 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/30"
            title={t('Save a version you can restore to')}
          >
            {saveStatus === 'error' ? t('Retry save') : t('Save version')}
          </button>
        )}
      </div>
    </div>
  );
};

export default CollaborativeDocumentEditor;
